import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import { bindActionCreators } from 'redux';

import fetchSalon from 'actions/fetchSalon.action';
import bePutSendSms from 'actions/salon/bePutSendSms.action';
import beGetClientSmsText from 'actions/sms/beGetClientSmsText.action';
import bePutClientSmsText from 'actions/sms/bePutClientSmsText.action';
import bePutSalonFlagSettings from 'actions/sms/bePutSalonFlagSettings.action';
import IMAGE_PHONE from 'assets/images/clients/reminders/appointment-phone-mock.png';
import IMG_MEGAPHONE from 'assets/images/icons/megaphone-black.svg';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import Toggler from 'components/form/Toggler.component';
import Badge from 'components/generic/Badge.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Icon from 'components/generic/Icon.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import Space from 'components/generic/Space.component';
import Tooltip from 'components/generic/Tooltip.component';
import UpgradePlanButton from 'components/hr/timeOffSplash/UpgradePlanButton.component';
import CurrencySign from 'components/price/CurrencySign.component';
import DacCost from 'components/price/DacCost.component';
import PlanCost from 'components/price/PlanCost.component';
import SmsPrice from 'components/price/SmsPrice.component';
import PLAN_NAME from 'constants/plan.const';
import SMS_TYPE from 'constants/salon/smsType.const';
import TRACK_EVENT_TYPE from 'constants/salon/trackEventType.const';
import S from 'constants/string.const';
import useAfn from 'hooks/useAfn.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { withTheme } from 'providers/Theme.provider';
import {
  selectCurrentSalonBilling,
  selectCurrentSalonConfirmationSms,
  selectCurrentSalonDigitalCardAddOn,
  selectCurrentSalonId,
  selectCurrentSalonPricingPlan,
} from 'selectors/currentSalon.selectors';
import { selectBillingPlan } from 'selectors/settings.selectors';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import logger from 'utils/logger.util';
import salonSubscription from 'utils/salons/salonSubscription.util';

import { trackEvent } from 'tracking/actions';

import CN from './RemindersLayout.module.scss';

const L = logger('Confirmation');

const determineAppointmentText = (t) =>
  [
    t('composite.confirmation.appointmentTextPart1'),
    t('composite.confirmation.appointmentTextPart2'),
    t('composite.confirmation.appointmentTextPart3'),
  ].join(S.space);

const Confirmation = ({
  tag,
  salonId,
  hasBilling,
  pricingPlan,
  bePutSendSms,
  isDac,
  defaultSmsType,
  bePutSalonFlagSettings,
  variant: variantFromProps,
  onSuccess,
}) => {
  const { t } = useTranslation();

  const variant = fromAnyToTrimmed(variantFromProps);
  const isInSmsPanel = variant === 'smsPanel';
  const isInBillingPage = variant === 'billing';
  const salonHasNoSubscription = salonSubscription(hasBilling, pricingPlan);

  const { val: renderModalBilling, yay: showModalBilling, nay: hideModalBilling } = useTip(false);
  const { val: renderModal, yay: showModal, nay: hideModal } = useTip(false);
  const { val: isUpgradePlanSelected, tip: toggleUpgradePlanSelected } = useTip(true);
  const { val: selectDac, tip: toggleSelectDac } = useTip(false);
  const { val: enableAddOn, set: setConfirmation } = useTip(isDac);
  const [smsType, setSmsType] = useState(defaultSmsType);

  const labelAddOn = isDac ? t('composite.confirmation.enabledAddOn') : t('composite.confirmation.optionalAddOn');
  const labelBillingButton = isDac ? t('common.enabled') : t('composite.confirmation.buyAddOn');
  const isSMSoff = smsType === SMS_TYPE.offRequested || smsType === SMS_TYPE.offUnspecified;

  const handleSelection = useCallback(() => {
    toggleSelectDac();
    toggleUpgradePlanSelected();
    trackEvent(TRACK_EVENT_TYPE.upgradeSmsSettings);
  }, [toggleSelectDac, toggleUpgradePlanSelected]);

  const [sendSmsConfirmationResult, handleSendConfirmation] = useAfn(async () => {
    const sendConfirm = !isDac && isSMSoff ? SMS_TYPE.onRequested : SMS_TYPE.offRequested;

    const sendResult = await bePutSalonFlagSettings({ data: { send_confirmation_sms: sendConfirm } });

    if (!sendResult.is.ok) {
      L.warn('handleSendConfirmation()', 'sendResult:', sendResult);
      return;
    }
    setSmsType(sendResult.json.send_confirmation_sms);

    const digitalCardResult = await bePutSendSms({
      salonId,
      digital_card_add_on: !enableAddOn,
    });

    if (!digitalCardResult.is.ok) {
      L.warn('handleSendConfirmation()', 'digitalCardResult:', digitalCardResult);
      return;
    }

    setConfirmation(digitalCardResult.json.digital_card_add_on);
    hideModal();

    onSuccess?.(); // Display  successful modal in smsLogPanel after update

    trackEvent(TRACK_EVENT_TYPE.enableDacSettings);

    L.unignore('handleSendConfirmation()', 'fetchSalon:', fetchSalon(salonId));
  }, [
    isDac,
    isSMSoff,
    bePutSalonFlagSettings,
    bePutSendSms,
    salonId,
    enableAddOn,
    setConfirmation,
    hideModal,
    onSuccess,
  ]);

  const handleModalToggle = useCallback(() => {
    if (pricingPlan === PLAN_NAME.advanced || pricingPlan === PLAN_NAME.supercharged) {
      handleSendConfirmation();
      return;
    }

    showModal();
  }, [handleSendConfirmation, pricingPlan, showModal]);

  return (
    <div data-bem="Confirmation" className={CN.offset} data-tag={fromAnyToTrimmed(tag)}>
      {isInSmsPanel && !isDac && (
        <div data-bem="Confirmation__button-add-plan" onClick={handleModalToggle}>
          {t('composite.confirmation.addButton')}
        </div>
      )}

      {isInBillingPage && (
        <div
          data-bem="Confirmation__button-billing"
          onClick={isDac ? alwaysNoop : handleModalToggle}
          className={cn({
            [CN.buttonByDac]: true,
            [CN.disabled]: isDac,
          })}
        >
          <Badge name={labelBillingButton} variant={isDac ? 'fine' : 'attention'} heigh="md"></Badge>
        </div>
      )}

      {!isInSmsPanel && !isInBillingPage && (
        <div>
          <div className={CN.pageTitleContainer}>
            <div className={CN.pageTitle}>
              <img src={IMG_MEGAPHONE} alt="Reminders" className={CN.icoMegaphone} />
              {t('composite.confirmation.title')}
            </div>
            <div className={CN.pageSubTitle}>{t('composite.confirmation.subTitle')}</div>
          </div>
          <div className={CN.container}>
            <div className={CN.panel}>
              <div className={CN.gap}>
                <div className={CN.containerLabelCalendar}>
                  <div>
                    {t('composite.confirmation.labelCalendarPart1')}{' '}
                    <span className={CN.bold}>{t('composite.confirmation.labelCalendarPart2')}</span>{' '}
                    {t('composite.confirmation.labelCalendarPart3')}
                  </div>
                </div>
                <div className={CN.flex}>
                  <div className={CN.contentLeft}>
                    <div className={CN.gapRight}>
                      <div className={CN.header}>
                        <div className={CN.panelTitle}>{t('composite.confirmation.panelTitle')}</div>

                        <div className={CN.controls}>
                          {salonHasNoSubscription ? (
                            <Badge name={labelAddOn} variant="success">
                              <Tooltip content={t('composite.confirmation.tooltipText')} variant="top">
                                <Icon variant="info" width="lg" />
                              </Tooltip>
                            </Badge>
                          ) : (
                            <div className={CN.badgeContainer}>
                              <Badge name={t('composite.badge.unlimited')} variant="unlimited" />
                              <Badge name={t('composite.badge.includeInPlan')} variant="fine" />
                            </div>
                          )}
                          {!isDac ? (
                            <Toggler onClick={handleModalToggle} selected={enableAddOn} />
                          ) : (
                            <Toggler onClick={handleSendConfirmation} selected={enableAddOn} />
                          )}
                        </div>
                      </div>

                      <div className={CN.text}>
                        <div className={CN.highlighted}>{t('composite.confirmation.appointmentTitle')}</div>
                        {determineAppointmentText(t)}
                      </div>
                    </div>
                  </div>
                  <div className={cn([CN.contentRight])}>
                    <img src={IMAGE_PHONE} alt="sms phone graphic" className={CN.phoneImage} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {sendSmsConfirmationResult.isLoading && <OverlaySpinner />}

      <SalonModal tag="Confirmation__modal--dac" isOpen={renderModal} variant="large" withFooter>
        <SalonModal.Header>
          {t('composite.confirmation.cardsModal.title')}
          <Button variant="empty" onClick={hideModal}>
            <Icon variant="close" />
          </Button>
        </SalonModal.Header>
        <SalonModal.Body>
          <div className={CN.modalContainer}>
            <div className={CN.modalSubtitle}>{t('composite.confirmation.cardsModal.subTitle')}</div>
            <div className={CN.row}>
              <div
                className={cn({
                  [CN.col]: true,
                  [CN.selected]: isUpgradePlanSelected,
                  [CN.disabled]: !salonHasNoSubscription,
                })}
                role="button"
                onClick={handleSelection}
                tabIndex={0}
              >
                <div className={CN.planLabel}>
                  <Badge name={t('composite.badge.recommended')} variant="slick" />
                </div>

                <div className={CN.colTitle}>
                  {t('composite.confirmation.cardsModal.plan1Title')}{' '}
                  <Icon variant={isUpgradePlanSelected ? 'markCircle' : 'markCircleUnselected'} />
                </div>
                <div className={CN.colSubtitle}>
                  {t('composite.confirmation.cardsModal.additional')}
                  <div className={CN.textBigger}>
                    <PlanCost />/
                  </div>
                  {t('composite.confirmation.cardsModal.month')}
                </div>
                <div className={CN.list}>
                  <span className={CN.listItem}>{t('composite.confirmation.cardsModal.plan1Bullet1')}</span>
                  <span className={CN.listItem}>{t('composite.confirmation.cardsModal.plan1Bullet2')}</span>
                  <span className={CN.listItem}>{t('composite.confirmation.cardsModal.plan1Bullet3')}</span>
                  <span className={CN.listItem}>{t('composite.confirmation.cardsModal.plan1Bullet4')}</span>
                  <span className={CN.listItem}>{t('composite.confirmation.cardsModal.plan1Bullet5')}</span>
                  <span className={CN.listItem}>{t('composite.confirmation.cardsModal.plan1Bullet6')}</span>
                  <span className={CN.listItem}>{t('composite.confirmation.cardsModal.plan1Bullet7')}</span>
                </div>
              </div>

              <div
                className={cn({
                  [CN.col]: true,
                  [CN.selected]: selectDac,
                })}
                role="button"
                onClick={handleSelection}
                tabIndex={0}
              >
                <div className={CN.colTitle}>
                  {t('composite.confirmation.cardsModal.plan2Title')}{' '}
                  <Icon variant={selectDac ? 'markCircle' : 'markCircleUnselected'} />
                </div>
                <div className={CN.colSubtitle}>
                  {t('composite.confirmation.cardsModal.additional')}
                  <div className={CN.textBigger}>
                    <DacCost />
                  </div>
                  {t('composite.confirmation.cardsModal.month')}
                </div>
                <p>
                  {t('composite.confirmation.cardsModal.plan2DescriptionPart1')} <CurrencySign />
                  <SmsPrice /> {t('composite.confirmation.cardsModal.plan2DescriptionPart2')}
                </p>
                <p>{t('composite.confirmation.cardsModal.plan2Note')}</p>
              </div>
            </div>
          </div>
        </SalonModal.Body>
        <SalonModal.Footer>
          <div className={CN.modalButtons}>
            <ExternalLink variant="link">
              <div className={CN.linkHelp}>{t('common.buttons.needHelp')}</div>
            </ExternalLink>
            {salonHasNoSubscription & isUpgradePlanSelected ? (
              <UpgradePlanButton text={t('composite.confirmation.cardsModal.upgradeButton')} />
            ) : (
              <>
                {!hasBilling ? (
                  <Button variant="primary" casing="dialog" onClick={showModalBilling}>
                    {t('common.buttons.enable')}
                  </Button>
                ) : (
                  <Button variant="primary" casing="dialog" onClick={handleSendConfirmation} disabled={!selectDac}>
                    {t('common.buttons.enable')}
                  </Button>
                )}
              </>
            )}
          </div>
        </SalonModal.Footer>
      </SalonModal>

      <SalonModal tag="Confirmation__modal--billing" isOpen={renderModalBilling}>
        <SalonModal.Body>
          <span className={CN.modalTitle}>{t('composite.confirmation.billingDialogTitle')}</span>
          <Space>
            <span className={CN.modalText}>{t('composite.confirmation.billingDialogDescription')}</span>
          </Space>
        </SalonModal.Body>
        <SalonModal.Footer>
          <Button variant="slim" onClick={hideModalBilling}>
            {t('common.buttons.closeUpp')}
          </Button>
        </SalonModal.Footer>
      </SalonModal>
    </div>
  );
};

const mapStateToProps = (state) => ({
  salonId: selectCurrentSalonId(state),
  billingPlan: selectBillingPlan(state),
  hasBilling: selectCurrentSalonBilling(state),
  pricingPlan: selectCurrentSalonPricingPlan(state),
  isDac: selectCurrentSalonDigitalCardAddOn(state),
  defaultSmsType: selectCurrentSalonConfirmationSms(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      bePutSalonFlagSettings,
      beGetClientSmsText,
      bePutClientSmsText,
      fetchSalon,
      bePutSendSms,
    },
    dispatch,
  );

const ConfirmationHoc = connect(mapStateToProps, mapDispatchToProps)(withTheme()(Confirmation));

export default ConfirmationHoc;
